import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { CurrencyDollar, TrendUp, TrendDown, ChartPie, Users, Target, ArrowUp, ArrowDown } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6366F1", "#F97316"];

export function AdminDashboard({ year, month }) {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { year };
        if (month) params.month = month;
        const { data: d } = await axios.get(`${API}/admin/dashboard`, { params, headers: { Authorization: `Bearer ${token}` } });
        setData(d);
      } catch { }
      setLoading(false);
    };
    load();
  }, [year, month, token]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!data) return <p className="text-slate-500">Error al cargar datos</p>;

  const kpis = [
    { label: "Ingresos", value: fmt(data.total_income), icon: CurrencyDollar, color: "bg-emerald-50 text-emerald-700", iconColor: "text-emerald-500", show: true },
    { label: "Gastos", value: fmt(data.total_expense), icon: TrendDown, color: "bg-red-50 text-red-700", iconColor: "text-red-500", show: isAdmin },
    { label: "Utilidad", value: fmt(data.profit), icon: TrendUp, color: data.profit >= 0 ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700", iconColor: data.profit >= 0 ? "text-blue-500" : "text-red-500", show: isAdmin },
    { label: "Margen", value: `${data.margin}%`, icon: ChartPie, color: "bg-purple-50 text-purple-700", iconColor: "text-purple-500", show: isAdmin },
    { label: "Meta", value: fmt(data.current_goal), icon: Target, color: "bg-amber-50 text-amber-700", iconColor: "text-amber-500", show: true },
    { label: "Avance Meta", value: `${data.goal_progress}%`, icon: Target, color: data.goal_progress >= 100 ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700", iconColor: data.goal_progress >= 100 ? "text-emerald-500" : "text-orange-500", show: true },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.filter(k => k.show).map((k, i) => (
          <div key={i} className={`rounded-xl p-4 ${k.color}`} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-1">
              <k.icon size={18} weight="duotone" className={k.iconColor} />
              <span className="text-xs font-medium opacity-70">{k.label}</span>
            </div>
            <p className="text-lg font-bold">{k.value}</p>
          </div>
        ))}
      </div>

      {/* YoY Comparison */}
      {isAdmin && data.prev_year_income !== null && (
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className={`p-2 rounded-full ${data.yoy_growth >= 0 ? "bg-emerald-100" : "bg-red-100"}`}>
            {data.yoy_growth >= 0 ? <ArrowUp size={20} className="text-emerald-600" /> : <ArrowDown size={20} className="text-red-600" />}
          </div>
          <div>
            <p className="text-sm text-slate-500">Comparativo vs {year - 1}</p>
            <p className={`text-lg font-bold ${data.yoy_growth >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {data.yoy_growth >= 0 ? "+" : ""}{data.yoy_growth}% | Anterior: {fmt(data.prev_year_income)}
            </p>
          </div>
        </div>
      )}

      {/* Monthly Trends Chart */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-sm font-semibold text-slate-500 mb-4 uppercase tracking-wider">Tendencia Mensual {year}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.monthly_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
            {isAdmin && <Bar dataKey="expense" name="Gastos" fill="#EF4444" radius={[4, 4, 0, 0]} />}
            <Line type="monotone" dataKey="goal" name="Meta" stroke="#F59E0B" strokeWidth={2} dot={false} strokeDasharray="5 5" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top 10 Clients */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider flex items-center gap-2">
            <Users size={16} /> Top 10 Clientes (Compras)
          </h3>
          <div className="space-y-2">
            {(data.top_clients || []).map((c, i) => {
              const maxVal = data.top_clients[0]?.total || 1;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`top-client-${i}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{c.client}</p>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                      <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${(c.total / maxVal) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-slate-600 whitespace-nowrap">{fmt(c.total)}</span>
                </div>
              );
            })}
            {(!data.top_clients || data.top_clients.length === 0) && <p className="text-sm text-slate-400">Sin datos</p>}
          </div>
        </div>

        {/* Top Potential Clients */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider flex items-center gap-2">
            <TrendUp size={16} /> Clientes con Mayor Potencial
          </h3>
          <div className="space-y-2">
            {(data.top_potential || []).map((c, i) => {
              const maxVal = data.top_potential[0]?.potential || 1;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`top-potential-${i}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{c.client}</p>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${(c.potential / maxVal) * 100}%` }} />
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-slate-600">{fmt(c.potential)}</span>
                    <p className="text-[10px] text-slate-400">{c.count} cotiz.</p>
                  </div>
                </div>
              );
            })}
            {(!data.top_potential || data.top_potential.length === 0) && <p className="text-sm text-slate-400">Sin datos</p>}
          </div>
        </div>
      </div>

      {/* Expenses by Category (Admin only) */}
      {isAdmin && data.expenses_by_category && data.expenses_by_category.length > 0 && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider">Gastos por Categoria</h3>
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={data.expenses_by_category} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                  {data.expenses_by_category.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top by Profit (Admin only) */}
      {isAdmin && data.top_profit && data.top_profit.length > 0 && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wider flex items-center gap-2">
            <CurrencyDollar size={16} /> Clientes con Mayor Utilidad
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.top_profit.slice(0, 10).map((c, i) => (
              <div key={i} className="p-3 bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-lg" data-testid={`top-profit-${i}`}>
                <p className="text-xs text-slate-500">#{i + 1}</p>
                <p className="text-sm font-semibold text-slate-700 truncate">{c.client}</p>
                <p className="text-lg font-bold text-emerald-700">{fmt(c.total)}</p>
                <p className="text-[10px] text-slate-400">{c.count} operaciones</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
